import { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Platform,
  TouchableOpacity,
  Dimensions,
  StyleSheet,
  Image,
  Animated,
  Easing,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Text } from '@components/Restyle';
import { Confetti } from '@components/Tutorialization/Confetti';
import {
  widthPixel,
  heightPixel,
  pixelSizeHorizontal,
  pixelSizeVertical,
} from '@utils/normalise';

import { successCheckGif } from '../../../assets/Tutorials/Project';

import type React from 'react';

interface CreatedProjectModalProps {
  visible: boolean;
  title: string;
  description: string;
  primaryBtnTitle: string;
  secondaryBtnTitle?: string;
  onClose?: () => void;
  primaryFn?: () => void;
  secondaryFn?: () => void;
}

const { width } = Dimensions.get('window');

export const CreatedProjectModal: React.FC<CreatedProjectModalProps> = ({
  visible,
  onClose,
  title = '',
  description = '',
  primaryBtnTitle = '',
  secondaryBtnTitle = '',
  primaryFn = () => {},
  secondaryFn = () => {},
}) => {
  const SHOW_DURATION = 1000; // ms
  const HIDE_DURATION = 500; // ms
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const translateYAnim = useRef(new Animated.Value(280)).current;
  const [shouldRender, setShouldRender] = useState(visible);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      Animated.parallel([
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: SHOW_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(translateYAnim, {
          toValue: 0,
          duration: SHOW_DURATION,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start();
    } else if (shouldRender) {
      Animated.parallel([
        Animated.timing(opacityAnim, {
          toValue: 0,
          duration: HIDE_DURATION,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(translateYAnim, {
          toValue: 40,
          duration: HIDE_DURATION,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start(() => setShouldRender(false));
    }
  }, [visible]);

  if (!shouldRender) return null;

  return (
    <Modal
      visible={true}
      transparent
      animationType='none'
      onRequestClose={onClose}>
      <Confetti visible={true} />
      {/* Backdrop */}
      <Animated.View
        style={[
          styles.backdrop,
          {
            paddingBottom:
              pixelSizeVertical(24) +
              (Platform.OS === 'android' ? insets.bottom : 0),
          },
        ]}
        pointerEvents='box-none'>
        <TouchableOpacity
          style={styles.fill}
          activeOpacity={1}
          onPress={onClose}
        />
        {/* Modal Container */}
        <Animated.View
          style={[
            styles.modalContainer,
            { transform: [{ translateY: translateYAnim }] },
          ]}>
          <TouchableOpacity activeOpacity={1} onPress={() => {}}>
            <Box style={styles.modalContent}>
              <Image
                source={successCheckGif}
                style={styles.messageBubbleImage}
                resizeMode='contain'
              />

              {/* Content */}
              <Box style={styles.textContainer}>
                <Text
                  style={styles.heading}
                  variant='heading2'
                  fontWeight='600'
                  fontSize={22}
                  lineHeight={32}
                  textAlign='center'
                  color='black'>
                  {title}
                </Text>

                <Text
                  textAlign='center'
                  color='grey04'
                  variant='body'
                  fontWeight='400'
                  fontSize={16}
                  lineHeight={21}>
                  {description}
                </Text>
              </Box>

              {/* Create Project Button */}
              <TouchableOpacity
                style={styles.createButton}
                onPress={primaryFn}
                activeOpacity={0.8}>
                <Text
                  fontSize={16}
                  color='white'
                  fontWeight='500'
                  lineHeight={16}
                  variant='labelSmall'>
                  {primaryBtnTitle}
                </Text>
              </TouchableOpacity>
              <Box marginTop='l' marginBottom='s'>
                <TouchableOpacity onPress={secondaryFn}>
                  <Text
                    fontSize={14}
                    color='greenSecondary'
                    fontWeight='500'
                    lineHeight={16}
                    variant='labelSmall'>
                    {secondaryBtnTitle}
                  </Text>
                </TouchableOpacity>
              </Box>
            </Box>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    // paddingBottom is applied inline at the callsite so it can include the
    // safe-area bottom inset (Android edge-to-edge). See usage above.
  },
  fill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContainer: {
    width: width * 0.9,
    maxWidth: 400,
    marginBottom: 0,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: pixelSizeVertical(32),
    paddingHorizontal: pixelSizeHorizontal(24),
    alignItems: 'center',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
    minHeight: heightPixel(360),
  },
  iconContainer: {
    marginBottom: 40,
    alignItems: 'center',
  },
  messageBubbleImage: {
    width: widthPixel(150),
    height: heightPixel(150),
  },
  backgroundContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ovalBackground: {
    position: 'absolute',
    right: -widthPixel(120),
    top: -heightPixel(120),
    width: widthPixel(325),
    height: heightPixel(300),
    backgroundColor: 'rgba(111, 177, 157, 0.13)',
    borderRadius: widthPixel(325),
    opacity: 1,
    zIndex: -1,
  },
  ovalBackgroundOverlay: {
    position: 'absolute',
    right: -widthPixel(90),
    top: -heightPixel(95),
    width: widthPixel(240),
    height: heightPixel(240),
    backgroundColor: 'rgb(111, 177, 157)',
    borderRadius: widthPixel(240),
    opacity: 1,
    zIndex: -1,
  },
  chatBubble: {
    width: widthPixel(120),
    height: heightPixel(80),
    backgroundColor: '#4ECDC4',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#4ECDC4',
    shadowOffset: {
      width: 0,
      height: 8,
    },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    transform: [
      { perspective: 1000 },
      { rotateX: '-10deg' },
      { rotateY: '5deg' },
    ],
  },

  textContainer: {
    marginBottom: pixelSizeVertical(20),
    paddingVertical: pixelSizeVertical(4),
  },
  heading: {
    marginBottom: pixelSizeVertical(18),
  },
  createButton: {
    backgroundColor: '#000000',
    paddingVertical: pixelSizeVertical(20),
    borderRadius: 12,
    width: '100%',
    alignItems: 'center',
  },
});
